import {
  Adjustment,
  Asset,
  Company,
  Loan,
  Note,
  TAdjustmentId,
  TAssetId,
  TCompanyId,
  TLoanId,
  User,
} from '/src/lib/models';

/**
 * List Adjustments API request.
 */
export type TListAdjustmentsRequest = {
  assetId?: TAssetId;
  companyId?: TCompanyId;
  loanId?: TLoanId;
};

/**
 * List Adjustments API response.
 */
export type TListAdjustmentsResponse = Adjustment[];

/**
 * Get Adjustment API response.
 */
export type TGetAdjustmentResponse = Adjustment | undefined;

/**
 * Create Adjustment page form data.
 */
export type CreateAdjustmentForm = {
  /**
   * Company requesting the Adjustment
   */
  company?: Company;
  /**
   * Loan being adjusted
   */
  loan?: Loan;
  /**
   * Asset to adjust
   */
  asset?: Asset;
  /**
   * Effective date of this Adjustment
   */
  effectiveDate: string;
  /**
   * Adjustment name
   */
  name: string;
  /**
   * Adjustment amount. Can be negative
   */
  amount?: string;
  /**
   * Adjustment notes
   */
  notes: Note[];
  /**
   * List of related documents
   */
  files: string[];
};

/**
 * Create Adjustment API request.
 */
export type TCreateAdjustmentRequest = {
  /**
   * Company requesting the Adjustment
   */
  company: TCompanyId;
  /**
   * Loan being adjusted
   */
  loan: TLoanId;
  /**
   * Effective date of this Adjustment
   */
  effectiveDate: string;
  /**
   * Adjustment name
   */
  name: string;
  /**
   * ID of the Asset being adjusted
   */
  assetId: TAssetId;
  /**
   * Asset value before adjustment
   */
  assetValue: number;
  /**
   * Adjustment amount. Can be negative
   */
  amount: number;
  /**
   * Adjustment notes
   */
  notes: Note[];
  /**
   * List of related documents
   */
  files: string[];
  /**
   * User requesting the Adjustment
   */
  user: User;
};

/**
 * Create Adjustment API response.
 */
export type TCreateAdjustmentResponse = Adjustment;

/**
 * Update Adjustment page form data.
 */
export type UpdateAdjustmentForm = {
  /**
   * Transaction type. Set to `Adjustment` in this case
   */
  __t: string;
  /**
   * Optional version number
   */
  __v?: number;
  /**
   * Adjustment ID
   */
  _id: TAdjustmentId;
  /**
   * Admin fee
   */
  adminFee: number;
  /**
   * Adjustment amount. Can be negative
   */
  amount: number;
  /**
   * Asset to be adjusted
   */
  asset: Asset;
  /**
   * Asset value before adjustment
   */
  assetValue: number;
  /**
   * Company that requested the Adjustment
   */
  company: TCompanyId;
  /**
   * Database creation date for this Adjustment
   */
  created: Date;
  /**
   * Diligence fee
   */
  diligenceFee: number;
  /**
   * Discount fee
   */
  discountFee: number;
  /**
   * Effective date of this Adjustment
   */
  effectiveDate: Date;
  /**
   * Facility fee
   */
  facilityFee: number;
  /**
   * List of related documents
   */
  files: string[];
  /**
   * Late fee
   */
  lateFee: number;
  /**
   * Loan being adjusted
   */
  loan: Loan;
  /**
   * Adjustment name
   */
  name: string;
  /**
   * Adjustment notes
   */
  notes: Note[];
  /**
   * Origination fee
   */
  originationFee: number;
  /**
   * Asset value after adjustment
   */
  resultingAssetValue: number;
  /**
   * User that requested the Adjustment
   */
  user: User;
};

export type TUpdateAdjustmentRequest = {
  /**
   * Transaction type. Set to `Adjustment` in this case
   */
  __t: string;
  /**
   * Optional version number
   */
  __v?: number;
  /**
   * Adjustment ID
   */
  _id: TAdjustmentId;
  /**
   * Admin fee
   */
  adminFee: number;
  /**
   * Adjustment amount. Can be negative
   */
  amount: number;
  /**
   * Asset to be adjusted
   */
  asset: {
    _id: string;
    name: string;
  };
  /**
   * Asset value before adjustment
   */
  assetValue: number;
  /**
   * Company that requested the Adjustment
   */
  company: TCompanyId;
  /**
   * Database creation date for this Adjustment
   */
  created: Date;
  /**
   * Diligence fee
   */
  diligenceFee: number;
  /**
   * Discount fee
   */
  discountFee: number;
  /**
   * Effective date of this Adjustment
   */
  effectiveDate: Date;
  /**
   * Facility fee
   */
  facilityFee: number;
  /**
   * List of related documents
   */
  files: string[];
  /**
   * Late fee
   */
  lateFee: number;
  /**
   * Loan being adjusted
   */
  loan: {
    _id: string;
    assetValue: number;
    id?: string;
    name: string;
  };
  /**
   * Adjustment name
   */
  name: string;
  /**
   * Adjustment notes
   */
  notes: Note[];
  /**
   * Origination fee
   */
  originationFee: number;
  /**
   * Asset value after adjustment
   */
  resultingAssetValue: number;
  /**
   * User that requested the Adjustment
   */
  user: {
    _id: string;
    displayName?: string;
  };
};

/**
 * Update Adjustment API response.
 */
export type TUpdateAdjustmentResponse = Adjustment | undefined;

/**
 * Delete Adjustment API response.
 */
export type TDeleteAdjustmentResponse = Adjustment;
